import PresentationReducerBuilder from "./build-presentation-reducer"
import {
    SOURCE_ITEM_REQUEST as ITEM_REQUEST,
    SOURCE_ITEM_RESPONSE as ITEM_RESPONSE,
    SOURCE_ITEM_SAVE_RESPONSE as ITEM_SAVE_RESPONSE,
    SOURCE_ITEM_CACHED_RESPONSE as ITEM_CACHED_RESPONSE,
    SOURCE_ITEM_SAVE_ERROR_RESPONSE as ITEM_SAVE_ERROR_RESPONSE,
    SOURCE_ITEM_DELETE_REQUEST as ITEM_DELETE_REQUEST,
    SOURCE_ITEM_DELETE_RESPONSE as ITEM_DELETE_RESPONSE,
    SOURCE_ITEM_DELETE_ERROR_RESPONSE as ITEM_DELETE_ERROR_RESPONSE,
    SOURCE_LIST_SEARCH_REQUEST as LIST_SEARCH_REQUEST,
    SOURCE_LIST_REQUEST as LIST_PAGE_REQUEST,
    SOURCE_LIST_RESPONSE as LIST_RESPONSE,
    SOURCE_LIST_CACHED_RESPONSE as LIST_CACHED_RESPONSE,
    SOURCE_SET_FILTER as SET_FILTER,
    SOURCE_SET_EDIT_MODE as SET_EDIT_MODE,
    SOURCE_UPDATE_FILTER_CRITERIA as UPDATE_FILTER_CRITERIA,
    SOURCE_CLEAR_FILTER_CRITERIA as CLEAR_FILTER_CRITERIA,
    SOURCE_UPDATE_IMAGE as UPDATE_IMAGE,
    SOURCE_UPDATE_MODEL as UPDATE_MODEL,
    SOURCE_APPLY_MODEL_UPDATE as APPLY_MODEL_UPDATE,
    SOURCE_CANCEL_MODEL_UPDATE as CANCEL_MODEL_UPDATE,
    SOURCE_ADD_NEW as ADD_NEW,
    SOURCE_RENDERED_LISTING_TEST_LINK,
    SOURCE_RENDERED_LISTING_CHECK_RESPONSE,
    SOURCE_RENDERED_LISTING_CHECK_ERROR_RESPONSE,
    SOURCE_RENDERED_LISTING_DISCOVER_REQUEST,
    SOURCE_RENDERED_LISTING_DISCOVER_RESPONSE,
    SOURCE_RENDERED_LISTING_DISCOVER_ERROR_RESPONSE
} from "../../actions/action-types"
import {
    toUI as sourceToUI,
    updateSourceChangeModel,
    SourceModel,
    SourceCreateModel,
    updateSourceCreateModel
} from "../../models/entities/source"
import {
    SourceFilterModel,
    updateSourceSearchFilterModel
} from "../../models/filters/source-filter"
import {
    renderedListingCheckedFields,
    renderedListingSourceType
} from "../../config/application-definitions"
import { apiErrorReason } from "../../epics/default-epics/epic-utils"

import { defaultSearchCriteria } from "../paging/source-paging-reducer"
import { serialiseSearchCriteria } from "../paging/paging-reducer-utils"

// What looking at the source URL found (rendered_listing_discover), cleared when the URL changes, since it described
// another page
const noRenderedListingDiscover = {
    renderedListingDiscover: null,
    isDiscoveringRenderedListing: false,
    renderedListingDiscoverError: null
}

// dashboard-api's check of a Rendered listing source's settings (rendered_listing_check), the link the editor is
// testing against its pattern, and what looking at the source URL found
const noRenderedListingCheck = {
    renderedListingCheck: null,
    isCheckingRenderedListing: false,
    renderedListingCheckFailed: false,
    renderedListingTestLink: "",
    ...noRenderedListingDiscover
}

// Whether a look at `url` is still for the source being edited. A look can finish after another source is opened
// or the URL is edited; source URLs are unique, so a look at another source's URL never matches.
const isForSourceUrl = ({ workingModel }, url) =>
    (workingModel.url || "").trim() === (url || "").trim()

// A check is on its way for the actions sourceRenderedListingCheckEpic answers
const checkPending = ({ workingModel }) => ({
    isCheckingRenderedListing:
        workingModel.type === renderedListingSourceType
})

const reducerBuilder = new PresentationReducerBuilder()
const defaultReducer = reducerBuilder
    .actionsToHandle({
        UPDATE_IMAGE,
        UPDATE_MODEL,
        APPLY_MODEL_UPDATE,
        CANCEL_MODEL_UPDATE,
        CLEAR_FILTER_CRITERIA,
        UPDATE_FILTER_CRITERIA,
        SET_FILTER,
        SET_EDIT_MODE,
        ITEM_REQUEST,
        ITEM_RESPONSE,
        ITEM_SAVE_RESPONSE,
        ITEM_CACHED_RESPONSE,
        ITEM_SAVE_ERROR_RESPONSE,
        ITEM_DELETE_REQUEST,
        ITEM_DELETE_RESPONSE,
        ITEM_DELETE_ERROR_RESPONSE,
        LIST_PAGE_REQUEST,
        LIST_SEARCH_REQUEST,
        LIST_RESPONSE,
        LIST_CACHED_RESPONSE,
        ADD_NEW
    })
    .updateChangeModel(updateSourceChangeModel)
    .updateCreateModel(updateSourceCreateModel)
    .workingModelToUI(sourceToUI)
    .workingModelClass(SourceModel)
    .workingFilterModelClass(SourceFilterModel)
    .updateSearchFilterModel(updateSourceSearchFilterModel)
    .canCancel(() => true)
    .createModelClass(SourceCreateModel)
    .canSave(
        (updateModel, file, workingModel) =>
            workingModel.name &&
            workingModel.name.length &&
            workingModel.url &&
            workingModel.url.length &&
            workingModel.type &&
            workingModel.type.length
    )
    .defaultState({
        workingModel: new SourceModel(),
        searchCriteria: serialiseSearchCriteria(defaultSearchCriteria),
        searchFilterModel: defaultSearchCriteria,
        workingFilterModel: new SourceFilterModel(),
        ...noRenderedListingCheck
    })
    .build()

export default (state = undefined, action) => {
    const defaultReduction = defaultReducer(state, action)
    const { type } = action
    switch (type) {
        case UPDATE_MODEL:
            return renderedListingCheckedFields.includes(action.name)
                ? {
                      ...defaultReduction,
                      ...checkPending(defaultReduction),
                      ...(action.name === "url" ? noRenderedListingDiscover : {})
                  }
                : defaultReduction

        case SET_EDIT_MODE:
        case ADD_NEW:
            return {
                ...defaultReduction,
                ...noRenderedListingCheck,
                ...checkPending(defaultReduction)
            }

        case SOURCE_RENDERED_LISTING_TEST_LINK:
            return {
                ...defaultReduction,
                renderedListingTestLink: action.testLink,
                ...checkPending(defaultReduction)
            }

        case SOURCE_RENDERED_LISTING_CHECK_RESPONSE:
            return {
                ...defaultReduction,
                renderedListingCheck: action.check,
                isCheckingRenderedListing: false,
                renderedListingCheckFailed: false
            }

        case SOURCE_RENDERED_LISTING_CHECK_ERROR_RESPONSE:
            return {
                ...defaultReduction,
                renderedListingCheck: null,
                isCheckingRenderedListing: false,
                renderedListingCheckFailed: true
            }

        case SOURCE_RENDERED_LISTING_DISCOVER_REQUEST:
            return {
                ...defaultReduction,
                isDiscoveringRenderedListing: true,
                renderedListingDiscoverError: null
            }

        case SOURCE_RENDERED_LISTING_DISCOVER_RESPONSE:
            if (!isForSourceUrl(defaultReduction, action.url)) {
                return defaultReduction
            }
            return {
                ...defaultReduction,
                renderedListingDiscover: action.discover,
                isDiscoveringRenderedListing: false
            }

        case SOURCE_RENDERED_LISTING_DISCOVER_ERROR_RESPONSE:
            if (!isForSourceUrl(defaultReduction, action.url)) {
                return defaultReduction
            }
            return {
                ...defaultReduction,
                renderedListingDiscover: null,
                isDiscoveringRenderedListing: false,
                // dashboard-api says why for a page it couldn't render or a URL it won't look at
                renderedListingDiscoverError:
                    apiErrorReason(action.error) ||
                    "Couldn't look at the page. Try again in a minute."
            }

        case CANCEL_MODEL_UPDATE:
        case ITEM_SAVE_RESPONSE:
            return { ...defaultReduction, ...noRenderedListingCheck }

        case ITEM_REQUEST:
            return { ...defaultReduction, ...noRenderedListingCheck }

        default:
            return defaultReduction
    }
}
